import { convertToMomentDateFormat, getMoment, getFormattedDateString, isValidDate } from 'utils/DateTimeUtils';
import { parseDateTime } from '@decisions/server/DecisionsFramework/Design/Properties/PropertyEditorService';
import { getUserCulture } from 'utils/UserCulture';
import moment from 'moment';

export interface ValidateDateResult {
  valid: boolean;
  suggestion: string;
  date: string;
  exact?: boolean;
}

export async function validateDate(
  currentText: string,
  selectedDateTimeValue: string,
  dateTimeFormat: string
): Promise<ValidateDateResult | undefined> {
  if (!currentText || currentText === selectedDateTimeValue) {
    return;
  }

  try {
    const result = await parseDateTime({
      dateText: currentText,
      dateTimeFormat,
    });

    if (result.IsValidInput) {
      let dateString = getMoment()
        .parseZone(result.Value)
        .format(convertToMomentDateFormat(dateTimeFormat));
      return { date: dateString, suggestion: `Set To - ${dateString}`, valid: true, exact: result.IsParsedExactly };
    } else {
      return { suggestion: 'Invalid Date Format', valid: false, date: selectedDateTimeValue };
    }
  } catch (error) {
    return;
  }
}

export function getDateFormat(isDateTimePicker?: boolean) {
  return isDateTimePicker ? getUserCulture().DateTimeFormat : getUserCulture().DateFormat;
}

export function getMomentDateFormat(isDateTimePicker?: boolean) {
  return convertToMomentDateFormat(getDateFormat(isDateTimePicker));
}

export function getTimeFormat() {
  return getUserCulture().TimeFormat;
}

export function getMomentTimeFormat() {
  return convertToMomentDateFormat(getTimeFormat());
}

export function getMomentAndDateString(
  format: string,
  date?: string | moment.Moment,
  keepInvalidSuggestion?: boolean,
  returnCurrentDateIfUndefined = true
) {
  if (getMoment().isMoment(date)) {
    return { parsedMoment: date, parsedDateString: getFormattedDateString(date, format) };
  } else if (isValidDate(date, format)) {
    return { parsedDateString: date, parsedMoment: getMoment()(date, format) };
  }
  if (!returnCurrentDateIfUndefined && !date) {
    return { parsedMoment: undefined, parsedDateString: '' };
  }
  let currentDate = getMoment()(); // today, but without H:MM:SS from "now."
  currentDate.hours(0);
  currentDate.minutes(0);
  currentDate.seconds(0);
  return { parsedMoment: currentDate, parsedDateString: keepInvalidSuggestion ? date?.toString() : '' };
}

function getDateFormatForMask(format: string) {
  if (!format) return '';
  const monthReg = /M/gi;
  const dateReg = /D/gi;
  const monthDigitCount = format.match(monthReg) || [];
  if (monthDigitCount.length < 2) format = format.replace(monthReg, 'MM');
  const dateDigitCount = format.match(dateReg) || [];
  if (dateDigitCount.length < 2) format = format.replace(dateReg, 'DD');
  return format;
}

export function getDateFormatMask(format: string, maskChar?: string) {
  format = getDateFormatForMask(format);
  return format.replace(/[a-zA-Z]/g, maskChar || '_');
}

export function getTimeFormatMask(format: string, maskChar?: string) {
  if (!format) return '';
  const hourReg = /h/gi;
  const hourDigitCount = format.match(hourReg) || [];
  if (hourDigitCount.length < 2) format = format.replace(hourReg, 'hh');
  return format.replace(/[a-zA-Z]/g, maskChar || '_');
}
